import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetPlannersByBuildingMutation,
  useGetPlannerBuildingsQuery,
} from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import { getCurrentId } from '@/services/correlation';
import { telemetry } from '@/services/telemetry';
import { EVENT_TYPES, TELEMETRY_COMPONENT } from '@/constants';
import { UNASSIGNED_OWNER_ID, type OwnerOption } from '@/types/planner.types';

export type OwnerFilterErrorStage = 'NONE' | 'BUILDING_ACCESS' | 'PLANNERS_BY_BUILDING';

export interface UseOwnerFilterOptionsResult {
  options: OwnerOption[];
  isLoading: boolean;
  error: OwnerFilterErrorStage;
  retry: () => void;
}

interface PlannerEntry {
  email: string;
  displayName: string;
}

// Backend's POST /v1/planners/by-building enforces buildingIds.MaxLength = 50.
// Planners can have access to >50 buildings, so we batch and union the results.
const BUILDING_IDS_BATCH_SIZE = 50;

const chunk = <T>(items: readonly T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
};

const dedupeAndSort = (entries: PlannerEntry[]): OwnerOption[] => {
  const seen = new Map<string, string>();
  for (const p of entries) {
    if (!p.email) continue;
    const id = p.email.trim().toLowerCase();
    if (!seen.has(id)) {
      seen.set(id, p.displayName ?? id);
    }
  }
  return Array.from(seen.entries())
    .map(([id, displayName]) => ({ id, displayName }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }));
};

// Module-scoped guard — truly session-scoped. Survives React 18 StrictMode's
// dev-only mount/unmount/mount cycle (which would otherwise reset a useRef and
// fire the network request twice on first render).
let sessionFetchStarted = false;

// Module-scoped cache of fetched peer options so they survive component
// unmount/remount (e.g. navigating away from and back to the event list).
let cachedPeerOptions: OwnerOption[] | null = null;

// Test-only escape hatch: lets unit tests reset the session-scoped guard
// between cases without forcing a module reload.
export function __resetOwnerFilterOptionsSessionForTests(): void {
  sessionFetchStarted = false;
  cachedPeerOptions = null;
}

export function useOwnerFilterOptions(): UseOwnerFilterOptionsResult {
  const { t } = useTranslation('planner');
  const { data: plannerBuildingsData } = useGetPlannerBuildingsQuery();
  const [getPlannersByBuilding] = useGetPlannersByBuildingMutation();

  const [peerOptions, setPeerOptions] = useState<OwnerOption[]>(cachedPeerOptions ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<OwnerFilterErrorStage>('NONE');

  // runFetchRef captures the latest closure so the effect can fire exactly
  // once per session without depending on the (often-reidentified) RTK Query
  // trigger functions.
  const runFetchRef = useRef<() => Promise<void>>(async () => {});

  const runFetch = useCallback(async () => {
    const buildingIds = plannerBuildingsData?.buildingIds ?? [];

    const correlationId = getCurrentId();
    const startedAt = Date.now();

    setIsLoading(true);
    setError('NONE');

    telemetry.trackEvent('OwnerFilter', EVENT_TYPES.START, {
      operationName: 'OwnerFilter.Resolve',
      component: TELEMETRY_COMPONENT,
      correlationId,
    });

    try {
      if (buildingIds.length === 0) {
        setPeerOptions([]);
        setError('NONE');
        telemetry.trackEvent('OwnerFilter', EVENT_TYPES.SUCCESS, {
          operationName: 'OwnerFilter.Resolve',
          component: TELEMETRY_COMPONENT,
          correlationId,
          buildingCount: '0',
          ownerCount: '0',
          durationMs: String(Date.now() - startedAt),
        });
        return;
      }

      const plannersResponses = await Promise.all(
        chunk(buildingIds, BUILDING_IDS_BATCH_SIZE).map((batch) =>
          getPlannersByBuilding({ buildingIds: batch }).unwrap()
        )
      );

      const anyFailed = plannersResponses.some((r) => !r.success || !r.data);
      if (anyFailed) {
        setPeerOptions([]);
        setError('PLANNERS_BY_BUILDING');
        telemetry.trackEvent('OwnerFilter', EVENT_TYPES.FAILURE, {
          operationName: 'OwnerFilter.Resolve',
          component: TELEMETRY_COMPONENT,
          correlationId,
          stage: 'PLANNERS_BY_BUILDING',
          errorType: 'EMPTY_OR_FAILED_RESPONSE',
          durationMs: String(Date.now() - startedAt),
        });
        return;
      }

      const flattened: PlannerEntry[] = plannersResponses.flatMap((r) =>
        (r.data ?? []).flatMap((bp) => bp.planners)
      );
      const deduped = dedupeAndSort(flattened);

      cachedPeerOptions = deduped;
      setPeerOptions(deduped);
      setError('NONE');

      telemetry.trackEvent('OwnerFilter', EVENT_TYPES.SUCCESS, {
        operationName: 'OwnerFilter.Resolve',
        component: TELEMETRY_COMPONENT,
        correlationId,
        buildingCount: String(buildingIds.length),
        ownerCount: String(deduped.length),
        durationMs: String(Date.now() - startedAt),
      });
    } catch (e) {
      const errMessage = e instanceof Error ? e.message : 'UNKNOWN';
      // We can't distinguish stages from a generic throw; assume planners stage
      // if we got past the access call. Best-effort classification.
      setPeerOptions([]);
      setError('PLANNERS_BY_BUILDING');
      telemetry.trackEvent('OwnerFilter', EVENT_TYPES.FAILURE, {
        operationName: 'OwnerFilter.Resolve',
        component: TELEMETRY_COMPONENT,
        correlationId,
        stage: 'PLANNERS_BY_BUILDING',
        errorType: errMessage,
        durationMs: String(Date.now() - startedAt),
      });
    } finally {
      setIsLoading(false);
    }
  }, [plannerBuildingsData?.buildingIds, getPlannersByBuilding]);

  // Always keep the ref pointing at the latest runFetch closure so retry()
  // and the mount effect use up-to-date trigger references.
  runFetchRef.current = runFetch;

  useEffect(() => {
    if (sessionFetchStarted) return;
    if (!plannerBuildingsData?.buildingIds) return;
    sessionFetchStarted = true;
    void runFetchRef.current();
  }, [plannerBuildingsData?.buildingIds]);

  const retry = useCallback(() => {
    sessionFetchStarted = true; // already true; explicit for clarity
    void runFetchRef.current();
  }, []);

  const options = useMemo<OwnerOption[]>(() => {
    const unassigned: OwnerOption = {
      id: UNASSIGNED_OWNER_ID,
      displayName: t('filter.ownerUnassigned'),
    };
    return [unassigned, ...peerOptions];
  }, [peerOptions, t]);

  return useMemo(() => ({ options, isLoading, error, retry }), [options, isLoading, error, retry]);
}
